import { z } from 'zod'
import { LocaleCode } from './enums.js'

/**
 * Provider-neutral mail contracts — PRD §12.7, §§11.6–11.8, §15.8.
 *
 * `MailService` owns templates, message identity, dispatch policy, outbox state,
 * suppression and observability; `EmailProvider` owns only configuration, sending,
 * outcome reconciliation and verified event normalization. Physical provider names may
 * differ from the ones recorded here; the semantics may not (§12.7).
 *
 * Hard rules encoded below:
 * - a payload contract carries no document text, credentials or capability links;
 * - an attempt pins its provider and configuration version, and the provider becomes
 *   immutable once the external call may have occurred — an in-flight or `UNKNOWN`
 *   attempt is never retargeted (§11.6, §15.8);
 * - outcomes are `ACCEPTED` / `REJECTED` / `UNKNOWN` with explicit retryability —
 *   never "HTTP 500 = retry";
 * - provider events are verified for provenance before normalization and deduped by
 *   provider + event identity;
 * - suppression is SignBento-owned, purpose-limited and portable across a provider
 *   cutover; raw provider payloads, OTPs, capability links and message bodies are
 *   never logged.
 */

const Id = z.string().min(1).max(128)
const IsoDateTime = z.string().datetime()
const Sha256Hex = z.string().regex(/^[0-9a-f]{64}$/)

/** Live and seam-only senders (register §4 V17-CFG-05/§15.8). */
export const MailProviderId = z.enum(['cloudflare', 'ses'])
export type MailProviderId = z.infer<typeof MailProviderId>

/** Logical message class — §12.7. */
export const MailMessageKind = z.enum([
  'auth_service',
  'managed_agreement',
  'account_service',
  /**
   * An operational alert for the deployment's own ops address (§14.3, OPS-01/02). Not customer
   * mail: it belongs to no account, carries machine vocabulary and numbers only, and its
   * recipient is the mail worker's secret rather than anything an intent can name.
   */
  'ops_alert',
])
export type MailMessageKind = z.infer<typeof MailMessageKind>

/**
 * Delivery lane, not a provider priority header — §11.6.
 * Auth/service and signing/status mail is never starved by bulk backlog (V17-AC-006).
 */
export const MailPriority = z.enum(['AUTH_SERVICE', 'SIGNING_STATUS', 'BULK'])
export type MailPriority = z.infer<typeof MailPriority>

/**
 * Credit policy — §11.7. `AUTH`/`SERVICE` mail consumes no delivery credit;
 * `MANAGED_AGREEMENT` obeys trial/credit policy (V17-AC-017).
 */
export const MailDeliveryClass = z.enum(['AUTH', 'SERVICE', 'MANAGED_AGREEMENT'])
export type MailDeliveryClass = z.infer<typeof MailDeliveryClass>

/** Exactly one recipient reference — §12.7. */
export const MailRecipientRef = z
  .object({
    recipient_ref: Id,
    kind: z.enum(['to', 'cc']),
  })
  .strict()
export type MailRecipientRef = z.infer<typeof MailRecipientRef>

/**
 * Related subjects a message may reference. Optional individually; a managed
 * agreement message always resolves an envelope (checked by the owning subsystem,
 * not here — M0 freezes shapes only).
 */
export const MailRelatedRefs = z
  .object({
    challenge_id: Id.optional(),
    /** An account-service message (the inactivity notice) names the notice it stands for. */
    notice_id: Id.optional(),
    envelope_id: Id.optional(),
    participant_id: Id.optional(),
    revision: z.number().int().positive().optional(),
  })
  .strict()
export type MailRelatedRefs = z.infer<typeof MailRelatedRefs>

/**
 * One alert in an `ops_alert` message (§14.3, OPS-01/02).
 *
 * Machine vocabulary only: the definition's stable id, the state it is in, the number the
 * evaluator read and the threshold it crossed. No address, no account id, no free text and no
 * user content can travel here — an alert's own "never log this" rule (OPS-04) applies to the
 * email exactly as it does to a log line, and the recipient is the dispatcher's own secret.
 */
export const OpsAlertItem = z
  .object({
    alert_id: z.string().min(1).max(128),
    /** `RESOLVED` is a return to OK; a firing alert is `FIRING` with `reminder` saying which kind. */
    state: z.enum(['FIRING', 'RESOLVED']),
    /** A reminder for an alert that is still firing, or the first message for its edge. */
    reminder: z.boolean(),
    /** The observed value; `null` when the metric answered nothing outstanding. */
    observed: z.number().nullable(),
    threshold: z.number(),
    window_seconds: z.number().int().positive().nullable(),
  })
  .strict()
export type OpsAlertItem = z.infer<typeof OpsAlertItem>

/**
 * The `ops_alert` message payload, carried inside the intent.
 *
 * Every other message kind resolves its content from an owning subsystem's row at send time; an
 * alert belongs to no account, and its content is generated from stored machine values, so it
 * travels here instead of in a protected payload. The shape is closed for that reason: there is
 * no field an address, a token or document content could be placed in. The link back to the ops
 * page is composed by the dispatcher from the deployment's own origin, so no URL travels either.
 */
export const OpsAlertPayload = z
  .object({
    alerts: z.array(OpsAlertItem).min(1).max(64),
  })
  .strict()
export type OpsAlertPayload = z.infer<typeof OpsAlertPayload>

/**
 * Protected delivery payload contract — §12.7, §10.2.1.
 *
 * This object carries an opaque reference to an encrypted payload blob and nothing
 * else: no subject, body, document text, credentials or capability links may appear
 * in the contract, and `strict()` rejects unknown fields that try. The encrypted
 * delivery payload is stored separately from the challenge verifier and purged after
 * delivery, cancellation or expiry (V17-CFG-08).
 */
export const MailPayload = z
  .object({
    protected_payload_ref: z.string().min(1).max(512),
    content_profile_version: z.number().int().positive(),
    encryption_profile: z.string().min(1).max(64),
    retention_class: z.enum(['OUTBOX_PAYLOAD', 'OTP_DELIVERY_PAYLOAD']),
  })
  .strict()
export type MailPayload = z.infer<typeof MailPayload>

/** Retry/attempt envelope for a logical message — bounded retries + dead-letter (§11.5). */
export const MailAttemptPolicy = z
  .object({
    max_attempts: z.number().int().positive(),
    retry_on: z.array(z.enum(['REJECTED', 'UNKNOWN'])).min(1),
  })
  .strict()
export type MailAttemptPolicy = z.infer<typeof MailAttemptPolicy>

/** Dispatch intent for one logical message — §12.7. */
export const MailIntent = z
  .object({
    logical_message_id: z.string().min(1).max(255),
    message_kind: MailMessageKind,
    delivery_class: MailDeliveryClass,
    priority: MailPriority,
    template_id: z.string().min(1).max(128),
    template_version: z.number().int().positive(),
    locale: LocaleCode,
    recipient: MailRecipientRef,
    /** Approved sending identity reference (never an address literal in the contract). */
    sender_identity_ref: z.string().min(1).max(128),
    related: MailRelatedRefs,
    expires_at: IsoDateTime,
    scheduled_at: IsoDateTime.optional(),
    not_after: IsoDateTime.optional(),
    cancelled_at: IsoDateTime.optional(),
    payload: MailPayload,
    attempt_policy: MailAttemptPolicy,
    /**
     * Present exactly for `message_kind: 'ops_alert'` — the alert content travels with the intent
     * because it belongs to no account and no protected payload row exists to resolve at send
     * time (see {@link OpsAlertPayload}).
     */
    ops_alert: OpsAlertPayload.optional(),
  })
  .strict()
  .superRefine((intent, ctx) => {
    if (intent.message_kind === 'ops_alert') {
      if (intent.delivery_class !== 'SERVICE') {
        ctx.addIssue({
          code: 'custom',
          message: 'an ops alert is service mail and is never charged as a managed agreement',
          path: ['delivery_class'],
        })
      }
      if (intent.ops_alert === undefined) {
        ctx.addIssue({
          code: 'custom',
          message: 'an ops alert intent carries its alert payload',
          path: ['ops_alert'],
        })
      }
    } else if (intent.ops_alert !== undefined) {
      ctx.addIssue({
        code: 'custom',
        message: 'only an ops alert intent carries an ops alert payload',
        path: ['ops_alert'],
      })
    }
    if (intent.scheduled_at !== undefined && intent.not_after !== undefined) {
      if (Date.parse(intent.scheduled_at) > Date.parse(intent.not_after)) {
        ctx.addIssue({
          code: 'custom',
          message: 'not_after must not precede scheduled_at',
          path: ['not_after'],
        })
      }
    }
    const earliest = intent.scheduled_at ?? intent.cancelled_at
    if (earliest !== undefined && Date.parse(intent.expires_at) < Date.parse(earliest)) {
      ctx.addIssue({
        code: 'custom',
        message: 'expires_at must not precede scheduled_at',
        path: ['expires_at'],
      })
    }
  })
export type MailIntent = z.infer<typeof MailIntent>

/** Outcome vocabulary — never inferred from an HTTP status (§11.6, V17-CFG-07). */
export const MailDispatchOutcome = z.enum(['ACCEPTED', 'REJECTED', 'UNKNOWN'])
export type MailDispatchOutcome = z.infer<typeof MailDispatchOutcome>

export const MailDispatchState = z.enum(['LEASED', 'IN_FLIGHT', 'COMPLETED'])
export type MailDispatchState = z.infer<typeof MailDispatchState>

/**
 * A retarget may only follow a definitively-not-sent attempt. `ACCEPTED`, `UNKNOWN`
 * and an in-flight attempt are never retargeted (§15.8).
 */
export const MailRetargetRef = z
  .object({
    attempt_id: Id,
    prior_outcome: z.enum(['ACCEPTED', 'REJECTED', 'UNKNOWN', 'IN_FLIGHT']),
  })
  .strict()
export type MailRetargetRef = z.infer<typeof MailRetargetRef>

/**
 * One dispatch attempt — §12.7, §§11.5–11.6.
 *
 * The provider and its configuration version are pinned on the attempt; there is no
 * field that can change them, and a retarget reference is only valid when the prior
 * attempt is known not to have reached the provider.
 */
export const MailDispatchAttempt = z
  .object({
    attempt_id: Id,
    logical_message_id: z.string().min(1).max(255),
    provider: MailProviderId,
    provider_config_version: z.number().int().positive(),
    lease_id: Id,
    fencing_token: z.number().int().positive(),
    requested_at: IsoDateTime,
    completed_at: IsoDateTime.optional(),
    observed_at: IsoDateTime.optional(),
    request_fingerprint: Sha256Hex,
    state: MailDispatchState,
    outcome: MailDispatchOutcome.optional(),
    provider_message_id: z.string().min(1).max(255).optional(),
    retryable: z.boolean().optional(),
    retarget_of: MailRetargetRef.optional(),
  })
  .strict()
  .superRefine((attempt, ctx) => {
    if (attempt.state === 'COMPLETED' && attempt.outcome === undefined) {
      ctx.addIssue({
        code: 'custom',
        message: 'a completed attempt records its outcome',
        path: ['outcome'],
      })
    }
    if (attempt.state === 'COMPLETED' && attempt.completed_at === undefined) {
      ctx.addIssue({
        code: 'custom',
        message: 'a completed attempt records completed_at',
        path: ['completed_at'],
      })
    }
    if (attempt.outcome === 'ACCEPTED' && attempt.provider_message_id === undefined) {
      ctx.addIssue({
        code: 'custom',
        message: 'ACCEPTED requires the provider message id',
        path: ['provider_message_id'],
      })
    }
    if (attempt.outcome === 'UNKNOWN' && attempt.provider_message_id !== undefined) {
      ctx.addIssue({
        code: 'custom',
        message: 'an UNKNOWN outcome carries no provider message id',
        path: ['provider_message_id'],
      })
    }
    if (attempt.retarget_of !== undefined && attempt.retarget_of.prior_outcome !== 'REJECTED') {
      ctx.addIssue({
        code: 'custom',
        message: 'an in-flight or possibly-delivered attempt is never retargeted (§15.8)',
        path: ['retarget_of', 'prior_outcome'],
      })
    }
  })
export type MailDispatchAttempt = z.infer<typeof MailDispatchAttempt>

/**
 * Minimal normalized provider event vocabulary — §11.7. Provider-specific payloads
 * are normalized by the adapter; nothing else reaches `MailService`.
 */
export const MailNormalizedEvent = z.enum([
  'ACCEPTED',
  'DELIVERED',
  'DEFERRED',
  'BOUNCED',
  'COMPLAINED',
  'SUPPRESSED',
  'REJECTED',
])
export type MailNormalizedEvent = z.infer<typeof MailNormalizedEvent>

/**
 * One verified provider event — §11.7, §15.8.
 *
 * Provider identity is `provider` + `provider_event_id` (§12.7). `occurred_at` is the
 * provider's instant and `recorded_at` is the server's; they are never conflated.
 * Provenance must be verified before normalization (V17-AC-013), and raw provider
 * payloads are never stored here or logged.
 */
export const MailProviderEvent = z
  .object({
    provider: MailProviderId,
    provider_event_id: z.string().min(1).max(255),
    /** Expected account/domain provenance the event is verified against. */
    expected_account_ref: z.string().min(1).max(128).optional(),
    expected_domain: z.string().min(1).max(255),
    provider_message_id: z.string().min(1).max(255).optional(),
    normalized_event: MailNormalizedEvent,
    provenance_verified: z.boolean(),
    occurred_at: IsoDateTime,
    recorded_at: IsoDateTime,
  })
  .strict()
export type MailProviderEvent = z.infer<typeof MailProviderEvent>

/** Canonical dedupe key for provider events — §11.7. */
export function mailProviderEventKey(event: {
  readonly provider: MailProviderId
  readonly provider_event_id: string
}): string {
  return `${event.provider}:${event.provider_event_id}`
}

/** Duplicate provider + event identities found in a batch (dedupe is provider-scoped). */
export function duplicateMailProviderEventKeys(
  events: readonly MailProviderEvent[],
): readonly string[] {
  const seen = new Set<string>()
  const duplicates = new Set<string>()
  for (const event of events) {
    const key = mailProviderEventKey(event)
    if (seen.has(key)) {
      duplicates.add(key)
    }
    seen.add(key)
  }
  return [...duplicates].sort()
}

/** Suppression scope — §11.7. SignBento-owned, purpose-limited, portable. */
export const MailSuppressionScope = z.enum(['ENVIRONMENT', 'ACCOUNT', 'PROVIDER'])
export type MailSuppressionScope = z.infer<typeof MailSuppressionScope>

export const MailSuppressionReason = z.enum([
  'HARD_BOUNCE',
  'COMPLAINT',
  'MANUAL',
  'POLICY',
  'INVALID_ADDRESS',
])
export type MailSuppressionReason = z.infer<typeof MailSuppressionReason>

export const MailSuppressionState = z.enum(['ACTIVE', 'RELEASED'])
export type MailSuppressionState = z.infer<typeof MailSuppressionState>

/** Evidence a suppression decision rests on; no raw payload, no message body. */
export const MailSuppressionSource = z
  .object({
    evidence_ref: z.string().min(1).max(512),
    provider: MailProviderId.optional(),
    provider_event_id: z.string().min(1).max(255).optional(),
    recorded_at: IsoDateTime,
  })
  .strict()
export type MailSuppressionSource = z.infer<typeof MailSuppressionSource>

/**
 * A suppression entry — §11.7, §12.7.
 * `recipient_key` is the normalized (non-plaintext) recipient identity; the review
 * policy and retention bound come from the register (V17-CFG-08).
 */
export const MailSuppression = z
  .object({
    id: Id,
    recipient_key: z.string().min(1).max(255),
    scope: MailSuppressionScope,
    reason: MailSuppressionReason,
    source: MailSuppressionSource,
    state: MailSuppressionState,
    created_at: IsoDateTime,
    released_at: IsoDateTime.optional(),
    review: z
      .object({
        reviewer_ref: z.string().min(1).max(128).optional(),
        reviewed_at: IsoDateTime.optional(),
      })
      .strict(),
    retention: z
      .object({
        policy_version: z.number().int().positive(),
        retain_until: IsoDateTime.optional(),
      })
      .strict(),
    /** Suppression survives a provider cutover (V17-AC-014). */
    portable_across_providers: z.literal(true),
  })
  .strict()
  .superRefine((suppression, ctx) => {
    if (suppression.state === 'RELEASED' && suppression.released_at === undefined) {
      ctx.addIssue({
        code: 'custom',
        message: 'a released suppression records released_at',
        path: ['released_at'],
      })
    }
    if (suppression.state === 'ACTIVE' && suppression.released_at !== undefined) {
      ctx.addIssue({
        code: 'custom',
        message: 'an active suppression has no released_at',
        path: ['released_at'],
      })
    }
  })
export type MailSuppression = z.infer<typeof MailSuppression>
